<template>
  <div class="reengagement-action">
    <label>
      {{ $t('AGENT_BOTS.REENGAGEMENT_ACTION.INTERVAL_LABEL') }}
      <select :value="action.interval" @change="onIntervalChange">
        <option :value="null" selected>
          {{ $t('AGENT_BOTS.REENGAGEMENT_ACTION.INTERVAL_PLACEHOLDER') }}
        </option>
        <option
          v-for="minutes in intervalOptions"
          :key="minutes"
          :value="minutes"
        >
          {{ formatInterval(minutes) }}
        </option>
      </select>
    </label>

    <label v-if="isWhatsAppInbox">
      {{ $t('AGENT_BOTS.REENGAGEMENT_ACTION.TYPE_LABEL') }}
      <select :value="actionType" @change="onTypeChange">
        <option value="text">
          {{ $t('AGENT_BOTS.REENGAGEMENT_ACTION.TYPE_TEXT') }}
        </option>
        <option value="template">
          {{ $t('AGENT_BOTS.REENGAGEMENT_ACTION.TYPE_TEMPLATE') }}
        </option>
      </select>
    </label>

    <label v-if="isWhatsAppInbox && actionType === 'template'">
      {{ $t('AGENT_BOTS.REENGAGEMENT_ACTION.TEMPLATE_LABEL') }}
      <select :value="action.template_name" @change="onTemplateChange">
        <option value="" selected>
          {{ $t('AGENT_BOTS.REENGAGEMENT_ACTION.TEMPLATE_PLACEHOLDER') }}
        </option>
        <option
          v-for="template in templates"
          :key="template.name"
          :value="template.name"
        >
          {{ template.name }}
        </option>
      </select>
    </label>

    <label v-if="actionType === 'text'">
      {{ $t('AGENT_BOTS.REENGAGEMENT_ACTION.MESSAGE_LABEL') }}
      <textarea
        :value="action.message"
        class="reengagement-action__textarea"
        rows="3"
        :placeholder="$t('AGENT_BOTS.REENGAGEMENT_ACTION.MESSAGE_PLACEHOLDER')"
        @input="onMessageInput"
      />
    </label>

    <p v-else-if="action.message" class="reengagement-action__preview">
      {{ action.message }}
    </p>

    <woot-button
      type="button"
      variant="clear"
      color-scheme="alert"
      size="small"
      icon="dismiss"
      class="reengagement-action__remove"
      @click="$emit('remove', index)"
    >
      {{ $t('AGENT_BOTS.REENGAGEMENT_ACTION.REMOVE_BUTTON') }}
    </woot-button>
  </div>
</template>

<script>
import { INBOX_TYPES } from 'shared/mixins/inboxMixin';

const INTERVAL_OPTIONS_IN_MINUTES = [
  2,
  5,
  10,
  15,
  20,
  30,
  60,
  120,
  240,
  360,
  480,
  720,
  1080,
  1440,
  2880,
  4320,
  5760,
  7200,
];

export default {
  props: {
    action: {
      type: Object,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    inboxId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      intervalOptions: INTERVAL_OPTIONS_IN_MINUTES,
    };
  },
  computed: {
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.inboxId);
    },
    isWhatsAppInbox() {
      return this.inbox?.channel_type === INBOX_TYPES.WHATSAPP;
    },
    templates() {
      return (
        this.$store.getters['inboxes/getWhatsAppTemplates'](this.inboxId) || []
      );
    },
    actionType() {
      if (!this.isWhatsAppInbox) return 'text';
      // Actions saved before the type selector existed don't have `type` set,
      // so fall back to inferring it from the presence of a template.
      return (
        this.action.type || (this.action.template_name ? 'template' : 'text')
      );
    },
  },
  methods: {
    formatInterval(minutes) {
      if (minutes < 60) {
        return this.$t('AGENT_BOTS.REENGAGEMENT_ACTION.INTERVAL_UNIT.MINUTES', {
          count: minutes,
        });
      }
      const hours = minutes / 60;
      return hours === 1
        ? this.$t('AGENT_BOTS.REENGAGEMENT_ACTION.INTERVAL_UNIT.HOUR')
        : this.$t('AGENT_BOTS.REENGAGEMENT_ACTION.INTERVAL_UNIT.HOURS', {
            count: hours,
          });
    },
    onIntervalChange(event) {
      this.$emit('update', this.index, {
        ...this.action,
        interval: event.target.value,
      });
    },
    onTypeChange(event) {
      const type = event.target.value;
      this.$emit('update', this.index, {
        ...this.action,
        type,
        template_name: '',
        message: type === 'text' ? this.action.message : '',
      });
    },
    onTemplateChange(event) {
      const templateName = event.target.value;
      const template = this.templates.find(t => t.name === templateName);
      const bodyComponent = template?.components?.find(
        component => component.type === 'BODY'
      );
      this.$emit('update', this.index, {
        ...this.action,
        template_name: templateName,
        message: bodyComponent?.text || '',
      });
    },
    onMessageInput(event) {
      this.$emit('update', this.index, {
        ...this.action,
        message: event.target.value,
      });
    },
  },
};
</script>

<style scoped lang="scss">
.reengagement-action {
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-normal);
  padding: var(--space-normal);
  margin-bottom: var(--space-normal);

  &__textarea {
    width: 100%;
    resize: vertical;
  }

  &__preview {
    background: var(--color-background);
    border-radius: var(--border-radius-normal);
    padding: var(--space-small) var(--space-normal);
    margin-bottom: var(--space-small);
  }

  &__remove {
    margin-top: var(--space-small);
  }
}
</style>
